import { supabase } from '@/integrations/supabase/client';

// Browser-compatible crypto functions
const getRandomValues = (array: Uint8Array): Uint8Array => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window.crypto.getRandomValues as any)(array);
  return array;
};

export const generateSalt = (): string => {
  const salt = new Uint8Array(16);
  getRandomValues(salt);
  return Array.from(salt)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};

export const hashPassword = async (password: string, salt: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(password + salt);
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

export const generateMFAToken = async (): Promise<string> => {
  const randomBytes = new Uint8Array(16);
  getRandomValues(randomBytes);
  const timestamp = Date.now().toString();
  const data = timestamp + Array.from(randomBytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
  
  const encoder = new TextEncoder();
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', encoder.encode(data));
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

export const generateMFACode = (): string => {
  // Generate a 6-digit code
  return Math.floor(100000 + Math.random() * 900000).toString();
};

export const storeMFACode = async (
  adminId: string,
  token: string,
  code: string
): Promise<void> => {
  const expiresAt = new Date();
  expiresAt.setMinutes(expiresAt.getMinutes() + 5); // Code expires in 5 minutes

  await supabase.from('mfa_verifications').insert({
    admin_id: adminId,
    token,
    code,
    expires_at: expiresAt.toISOString()
  });
};

export const verifyMFACode = async (
  token: string,
  code: string
): Promise<boolean> => {
  const { data, error } = await supabase
    .from('mfa_verifications')
    .select('*')
    .eq('token', token)
    .eq('code', code)
    .single();

  if (error || !data) {
    return false;
  }

  // Check if the code is expired
  const expiresAt = new Date(data.expires_at);
  if (expiresAt < new Date()) {
    return false;
  }

  // Delete the used code
  await supabase
    .from('mfa_verifications')
    .delete()
    .eq('token', token);

  return true;
};

export const logAdminAction = async (
  adminId: string | null,
  action: string,
  status: string,
  attemptedUsername?: string
): Promise<void> => {
  await supabase.from('admin_logs').insert({
    admin_id: adminId,
    action,
    status,
    ip_address: window.location.hostname,
    user_agent: navigator.userAgent,
    attempted_username: attemptedUsername
  });
}; 